import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sprout, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/connexion"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        <div className="flex items-center gap-2 mb-8">
          <Sprout className="h-6 w-6 text-primary" />
          <span className="font-display font-extrabold text-2xl tracking-tight">FoodFend</span>
        </div>

        {done ? (
          <div className="text-center py-8" data-testid="reset-success">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="font-display text-2xl mb-2">Mot de passe mis à jour</h1>
            <p className="text-muted-foreground text-sm">Redirection vers la connexion…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-4xl mb-2">Nouveau mot de passe</h1>
            <p className="text-muted-foreground text-sm mb-8">Choisissez un nouveau mot de passe pour votre compte.</p>

            {!token ? (
              <p className="text-sm text-destructive" data-testid="reset-invalid">
                Lien invalide. <Link to="/connexion" className="text-primary hover:underline">Retour à la connexion</Link>
              </p>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                <div className="space-y-1.5">
                  <Label htmlFor="pwd">Mot de passe</Label>
                  <Input id="pwd" type="password" data-testid="reset-password-input"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" minLength={6} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pwd2">Confirmer</Label>
                  <Input id="pwd2" type="password" data-testid="reset-confirm-input"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••" minLength={6} required />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
                <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="reset-submit-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Réinitialiser
                </Button>
              </form>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
